import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, TrendingUp, Wrench, Scale } from 'lucide-react';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import NationalLocalFunnel from '../../components/national/NationalLocalFunnel';
import NationalRelatedGuides from '../../components/national/NationalRelatedGuides';

const SITE = 'https://buildpilot.com.au';
const CANONICAL = `${SITE}/renovation-costs-australia`;

// National renovation cost bands by project type - broad orientation that
// funnels DOWN to the state-specific renovation cost pages.
const BANDS = [
  { tier: 'Cosmetic refresh', range: '$15k - $50k', note: 'Paint, flooring, fittings and fixtures - no walls moved, no plumbing relocated.' },
  { tier: 'Kitchen renovation', range: '$20k - $45k+', note: 'Cabinetry, benchtops, appliances and splashback. More if you move services.' },
  { tier: 'Bathroom renovation', range: '$15k - $35k+', note: 'Waterproofing, tiling, tapware and a vanity. Small room, high cost per m².' },
  { tier: 'Whole-home interior', range: '$60k - $180k+', note: 'Kitchen, bathrooms, flooring and paint across the house in one program.' },
  { tier: 'Structural / extension', range: '$2,500 - $4,500 / m²', note: 'Adding or reconfiguring space. Engineering, roofing and services add up fast.' },
];

const DRIVERS = [
  'Moving plumbing or electrical (every relocated point adds trades and time)',
  'Structural changes - removing walls needs engineering and steel',
  'Heritage or character homes (lead paint, lath-and-plaster, matching materials)',
  'Finish level - the gap between builder-grade and premium is enormous',
  'Access and age of the home - old services often need upgrading on the way',
];

const RenovationCostsAustraliaPage = () => {
  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
      { '@type': 'ListItem', position: 2, name: 'Guides', item: `${SITE}/guides` },
      { '@type': 'ListItem', position: 3, name: 'Renovation costs in Australia', item: CANONICAL },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>How Much Does a Renovation Cost in Australia? (2026) | BuildPilot</title>
        <meta name="description" content="A plain-English national guide to renovation costs in Australia for 2026 - kitchen, bathroom, whole-home and extension ranges, the cost drivers that bite, and live local prices for your state." />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
        <link rel="canonical" href={CANONICAL} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={CANONICAL} />
        <meta property="og:title" content="How Much Does a Renovation Cost in Australia? (2026)" />
        <meta property="og:description" content="National renovation cost bands by project type, the drivers that push them up, then live local prices for your state." />
        <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
      </Helmet>

      <Header variant="light" />

      <main data-testid="national-reno-cost-page">
        <section className="border-b border-gray-100 bg-[#fafbfc]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
            <p className="text-[11px] font-semibold uppercase tracking-[0.04em] text-[#1d8efe] mb-3">National guide · Australia</p>
            <h1 className="text-[32px] sm:text-[44px] font-bold text-[#1d1d1f] tracking-tight leading-[1.1] mb-4">
              How much does a renovation cost in Australia?
            </h1>
            <p className="text-[16px] sm:text-[18px] text-[#6b7078] leading-[1.6] max-w-2xl">
              Renovation budgets swing wildly with scope. Here's the national 2026 picture by project type, the things that quietly push the price up, and how to get a real quote where you live.
            </p>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="flex items-center gap-2 mb-5">
            <TrendingUp className="w-5 h-5 text-[#1d8efe]" />
            <h2 className="text-[24px] sm:text-[30px] font-bold text-[#1d1d1f] tracking-tight">Renovation cost by project type</h2>
          </div>
          <div className="space-y-3">
            {BANDS.map((b) => (
              <div key={b.tier} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-2xl border border-gray-200 p-5" data-testid={`reno-band-${b.tier.split(' ')[0].toLowerCase()}`}>
                <div className="sm:max-w-[60%]">
                  <h3 className="text-[16px] font-bold text-[#1d1d1f]">{b.tier}</h3>
                  <p className="text-[13.5px] text-[#86868b] mt-0.5 leading-[1.5]">{b.note}</p>
                </div>
                <div className="text-[18px] font-bold gradient-text-blue shrink-0">{b.range}</div>
              </div>
            ))}
          </div>
          <p className="text-[12.5px] text-gray-500 mt-4 leading-[1.55]">National 2026 ranges, labour and materials. Your home's age, access and finish level move you within them.</p>
        </section>

        <section className="bg-[#fafbfc] border-y border-gray-100">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
            <div className="flex items-center gap-2 mb-5">
              <Wrench className="w-5 h-5 text-[#1d8efe]" />
              <h2 className="text-[24px] sm:text-[30px] font-bold text-[#1d1d1f] tracking-tight">What pushes the price up</h2>
            </div>
            <ul className="space-y-2.5">
              {DRIVERS.map((d) => (
                <li key={d} className="flex items-start gap-3 text-[15px] text-[#1d1d1f] leading-[1.55]">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#1d8efe]" />
                  {d}
                </li>
              ))}
            </ul>
            <div className="mt-7 rounded-2xl border border-[#1d8efe]/20 bg-[#f0f6ff] p-5 flex items-start gap-3">
              <Scale className="w-5 h-5 text-[#1d8efe] mt-0.5 shrink-0" />
              <p className="text-[14px] text-[#1d1d1f] leading-[1.6]">
                Spending big on structural work? It's worth checking whether a knockdown rebuild is actually better value.{' '}
                <Link to="/knockdown-rebuild-explained" className="font-semibold text-[#1d8efe] hover:underline" data-testid="reno-to-kdr-link">
                  Renovate or rebuild? Read the explainer →
                </Link>
              </p>
            </div>
          </div>
        </section>

        <NationalLocalFunnel
          funnelTestId="reno-local-funnel"
          cardPrefix="reno-local-"
          eyebrow="Get a real number for your state"
          heading="National ranges orient you. Local prices and renovators get it done."
          intro="BuildPilot is live in South Australia and Victoria and expanding across Australia. Open your state for real renovation prices and the right people."
          states={[
            { code: 'SA', state: 'South Australia', city: 'Adelaide', primary: { label: 'Adelaide renovation costs', to: '/how-much-does-it-cost-to-renovate-in-adelaide' }, secondary: { label: 'Find the right people', to: '/renovations' } },
            { code: 'VIC', state: 'Victoria', city: 'Melbourne', primary: { label: 'Melbourne renovation costs', to: '/how-much-does-it-cost-to-renovate-in-melbourne' }, secondary: { label: 'Find the right people', to: '/home-builders-melbourne' } },
          ]}
          crossLink={{ label: 'Building new instead? See the cost to build in Australia', to: '/cost-to-build-a-house-australia', testid: 'reno-to-build-link' }}
        />

        <NationalRelatedGuides currentSlug="/renovation-costs-australia" />
      </main>

      <Footer />
    </div>
  );
};

export default RenovationCostsAustraliaPage;
